import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faPlus,
	faEdit,
	faTrash,
	faUsers,
	faSearch,
	faChild,
	faChevronDown,
	faChevronUp,
	faEnvelope,
	faPhone,
	faTimes,
} from "@fortawesome/free-solid-svg-icons";

import { PageContainer } from "../../components/layout";
import { Loading } from "../../components/common";
import ParentModal from "./components/ParentModal/ParentModal";

import {
	useGetAllParentDtosQuery,
	useDeleteParentMutation,
} from "../../store/api/parentApiSlice";
import { useGetAllStudentsIncludingInactiveQuery } from "../../store/api/studentApiSlice";

import styles from "./ParentsPage.module.css";

const ParentsPage = () => {
	// State management
	const [showParentModal, setShowParentModal] = useState(false);
	const [selectedParent, setSelectedParent] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [expandedParent, setExpandedParent] = useState(null);

	// API hooks
	const {
		data: parents = [],
		isLoading: parentsLoading,
		error: parentsError,
	} = useGetAllParentDtosQuery();

	const { data: students = [], isLoading: studentsLoading } =
		useGetAllStudentsIncludingInactiveQuery();

	const [deleteParent] = useDeleteParentMutation();

	// Create lookup for children by parent ID
	const childrenByParent = useMemo(() => {
		const lookup = {};
		students.forEach((student) => {
			if (student.parentId) {
				if (!lookup[student.parentId]) {
					lookup[student.parentId] = [];
				}
				lookup[student.parentId].push(student);
			}
		});
		return lookup;
	}, [students]);

	// Filter parents based on search
	const filteredParents = useMemo(() => {
		if (!searchTerm) return parents;

		const searchLower = searchTerm.toLowerCase();
		return parents.filter((parent) => {
			// Search in parent info
			const parentMatch =
				`${parent.firstName} ${parent.lastName}`
					.toLowerCase()
					.includes(searchLower) ||
				(parent.email && parent.email.toLowerCase().includes(searchLower)) ||
				(parent.phoneNumber && parent.phoneNumber.includes(searchTerm));

			// Search in children info
			const childrenMatch = childrenByParent[parent.id]?.some((child) =>
				`${child.firstName} ${child.lastName}`
					.toLowerCase()
					.includes(searchLower)
			);

			return parentMatch || childrenMatch;
		});
	}, [parents, searchTerm, childrenByParent]);

	// Handlers
	const handleAddParent = () => {
		setSelectedParent(null);
		setShowParentModal(true);
	};

	const handleEditParent = (e, parent) => {
		e.stopPropagation();
		setSelectedParent(parent);
		setShowParentModal(true);
	};

	const handleDeleteParent = async (e, parent) => {
		e.stopPropagation();
		if (
			window.confirm(
				`Are you sure you want to delete ${parent.firstName} ${parent.lastName}?`
			)
		) {
			try {
				await deleteParent(parent.id).unwrap();
			} catch (error) {
				console.error("Error deleting parent:", error);
				alert("Failed to delete parent. Please try again.");
			}
		}
	};

	const toggleExpand = (parentId) => {
		setExpandedParent(expandedParent === parentId ? null : parentId);
	};

	const closeModal = () => {
		setShowParentModal(false);
		setSelectedParent(null);
	};

	// Loading state
	if (parentsLoading) {
		return (
			<PageContainer>
				<div className={styles.loadingContainer}>
					<Loading message="Loading parent data..." />
				</div>
			</PageContainer>
		);
	}

	// Error state
	if (parentsError) {
		return (
			<PageContainer>
				<div className={styles.errorState}>
					<p>Error loading parents: {parentsError.message}</p>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<div className={styles.dashboardCard}>
				{/* Header */}
				<div className={styles.header}>
					<div className={styles.title}>
						<div className={styles.searchFilterRow}>
							<span className={styles.statPill}>
								<FontAwesomeIcon icon={faUsers} /> {parents.length} Parents
							</span>

							<div className={styles.searchBar}>
								<FontAwesomeIcon
									icon={faSearch}
									className={styles.searchIcon}
								/>
								<input
									type="text"
									placeholder="Search parents or children..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
								/>
								{searchTerm && (
									<button
										className={styles.clearSearch}
										onClick={() => setSearchTerm("")}
									>
										<FontAwesomeIcon icon={faTimes} />
									</button>
								)}
							</div>
						</div>
					</div>
					<button className={styles.addBtn} onClick={handleAddParent}>
						<FontAwesomeIcon icon={faPlus} /> Add New Parent
					</button>
				</div>

				{/* Table */}
				{filteredParents.length > 0 ? (
					<div className={styles.tableContainer}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th>Parent</th>
									<th>Email</th>
									<th>Phone</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredParents.map((parent) => (
									<React.Fragment key={parent.id}>
										<tr
											className={
												expandedParent === parent.id ? styles.expanded : ""
											}
											onClick={() => toggleExpand(parent.id)}
										>
											<td className={styles.nameCell}>
												<div className={styles.name}>
													<div className={styles.nameContent}>
														{parent.firstName} {parent.lastName}
													</div>
													<FontAwesomeIcon
														icon={
															expandedParent === parent.id
																? faChevronUp
																: faChevronDown
														}
														className={styles.expandIcon}
													/>
												</div>
											</td>
											<td>
												<div className={styles.contactInfo}>
													<div className={styles.emailDisplay}>
														<FontAwesomeIcon
															icon={faEnvelope}
															className={styles.contactIcon}
														/>
														{parent.email}
													</div>
												</div>
											</td>
											<td className={styles.phoneCell}>
												<div className={styles.contactInfo}>
													<div className={styles.phoneDisplay}>
														<FontAwesomeIcon
															icon={faPhone}
															className={styles.contactIcon}
														/>
														{parent.phoneNumber || "N/A"}
													</div>
												</div>
											</td>
											<td className={styles.actionsCell}>
												<div className={styles.actionButtons}>
													<button
														className={styles.editBtn}
														onClick={(e) => handleEditParent(e, parent)}
														title="Edit Parent"
													>
														<FontAwesomeIcon icon={faEdit} />
													</button>
													<button
														className={styles.deleteBtn}
														onClick={(e) => handleDeleteParent(e, parent)}
														title="Delete Parent"
													>
														<FontAwesomeIcon icon={faTrash} />
													</button>
												</div>
											</td>
										</tr>

										{/* Expanded Children View */}
										{expandedParent === parent.id && (
											<tr className={styles.detailsRow}>
												<td colSpan="4">
													<div className={styles.childrenDetails}>
														<h4>
															<FontAwesomeIcon
																icon={faChild}
																className={styles.childIcon}
															/>
															Children
														</h4>
														{studentsLoading ? (
															<div className={styles.loadingChildren}>
																<div className={styles.miniSpinner}></div>
																<span>Loading children...</span>
															</div>
														) : childrenByParent[parent.id]?.length > 0 ? (
															<table className={styles.childrenTable}>
																<thead>
																	<tr>
																		<th>Name</th>
																		<th>Date of Birth</th>
																		<th>Place of Birth</th>
																		<th>Citizenship</th>
																	</tr>
																</thead>
																<tbody>
																	{childrenByParent[parent.id].map((child) => (
																		<tr key={child.id}>
																			<td>
																				<div className={styles.childName}>
																					<FontAwesomeIcon
																						icon={faChild}
																						className={styles.childIconSmall}
																					/>
																					{child.firstName} {child.lastName}
																				</div>
																			</td>
																			<td>
																				{new Date(
																					child.dateOfBirth
																				).toLocaleDateString()}
																			</td>
																			<td>{child.placeOfBirth}</td>
																			<td>{child.citizenship}</td>
																		</tr>
																	))}
																</tbody>
															</table>
														) : (
															<div className={styles.noChildren}>
																No children found for this parent.
															</div>
														)}
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div className={styles.noResults}>
						<p>
							{searchTerm
								? "No parents match your search criteria."
								: "No parents found."}
						</p>
						{searchTerm && (
							<button onClick={() => setSearchTerm("")}>
								<FontAwesomeIcon icon={faTimes} /> Clear Search
							</button>
						)}
					</div>
				)}

				{/* Parent Modal */}
				{showParentModal && (
					<ParentModal
						parent={selectedParent}
						isOpen={showParentModal}
						onClose={closeModal}
					/>
				)}
			</div>
		</PageContainer>
	);
};

export default ParentsPage;
